import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);

  // Always persist to localStorage so closing a tab / restarting the browser
  // doesn't sign the rep out. Tokens get invalidated naturally when the
  // backend restarts (ephemeral JWT_SECRET) or after their 30-day expiry.
  // sessionStorage is read here only to migrate any tokens stored by the
  // previous build — after one fetch the user is on localStorage permanently.
  const getStoredToken = useCallback(() => {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const signOut = () => {
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
      setIsAuthenticated(false);
      setIsLoading(false);
    };
    // Verify, but only sign out on a *definitive* 401/403 (backend reachable and
    // says the token is bad). On a network error or 5xx — e.g. the backend is
    // mid-reboot — KEEP the token and retry, so a container restart never logs
    // the wall kiosk out. The token only dies when it's genuinely invalid.
    const verify = (attempt: number) => {
      const token = getStoredToken();
      if (!token) { setIsLoading(false); return; }
      fetch(`${API_BASE_URL}/api/verify-token`, { headers: { Authorization: `Bearer ${token}` } })
        .then(async res => {
          if (cancelled) return;
          if (res.ok) {
            const data = await res.json().catch(() => ({}));
            if (data.valid) {
              localStorage.setItem('auth_token', token);  // settle on localStorage
              sessionStorage.removeItem('auth_token');
              setIsAuthenticated(true);
              setUsername(data.username || null);
              setIsLoading(false);
              return;
            }
          }
          if (res.status === 401 || res.status === 403) { signOut(); return; }
          throw new Error(`verify status ${res.status}`);  // transient → retry
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 60) {
            window.setTimeout(() => verify(attempt + 1), attempt < 5 ? 3000 : 10000);
          } else {
            setIsLoading(false);  // give up verifying for now — token is preserved
          }
        });
    };
    verify(0);
    return () => { cancelled = true; };
  }, [getStoredToken]);

  const login = async (user: string, password: string): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('auth_token', data.token);
        // Clear any legacy sessionStorage token so the migration check in
        // getStoredToken stops returning a stale value.
        sessionStorage.removeItem('auth_token');
        setIsAuthenticated(true);
        setUsername(data.username);
        return null;
      }
      return data.error || 'Login failed';
    } catch {
      return 'Unable to connect to server';
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    setIsAuthenticated(false);
    setUsername(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, username, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};
